/**
 * SensitivityBadge — renders a document sensitivity tier (ITAR / CUI / PHI / ...)
 * as a colored pill, using the shared tokens in `src/lib/constants.ts` so badge
 * colors and labels are never hardcoded inline.
 *
 * Unknown tier strings (anything the backend sends that isn't a known tier) fall
 * back to a neutral style and render the raw value, so the UI never crashes on an
 * unexpected sensitivity value.
 */

import type { ReactNode } from 'react';
import {
  SENSITIVITY_BADGE_CLASSES,
  SENSITIVITY_LABELS,
} from '../lib/constants';
import { asSensitivityTier } from '../lib/retrieval';

const NEUTRAL_CLASS = 'bg-steel-100 text-steel-700';

export function SensitivityBadge({
  tier,
  full = false,
}: {
  /** The raw sensitivity string from the API (may be a known tier or not). */
  tier: string;
  /** When true, show the long descriptive label; otherwise the short code. */
  full?: boolean;
}): ReactNode {
  const known = asSensitivityTier(tier);
  const className = known ? SENSITIVITY_BADGE_CLASSES[known] : NEUTRAL_CLASS;
  // 'none' means we found no classification label in the source, not that the
  // content was assessed as non-sensitive. Say so: on a CUI system a badge
  // reading NONE is easily misread as an assurance.
  const short = known === 'none' ? 'unlabelled' : known;
  const label = known ? (full ? SENSITIVITY_LABELS[known] : short) : tier;

  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${className}`}
      title={known ? SENSITIVITY_LABELS[known] : tier}
      data-testid="sensitivity-badge"
    >
      {label}
    </span>
  );
}

/**
 * SensitivityBadgeList — renders a doc's sensitivity tiers as a row of badges.
 * Renders nothing when the list is empty.
 */
export function SensitivityBadgeList({
  sensitivity,
}: {
  sensitivity: string[];
}): ReactNode {
  if (!sensitivity || sensitivity.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {sensitivity.map((tier) => (
        <SensitivityBadge key={tier} tier={tier} />
      ))}
    </span>
  );
}

export default SensitivityBadge;
